"use client";

import clsx from "clsx";
import {AnimatePresence} from "framer-motion";
import {useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent} from "react";
import {useTranslation} from "react-i18next";
import GailAssistantLauncherButton from "./GailAssistantLauncherButton";
import GailAssistantSheet from "@/components/chat/GailAssistantSheet";
import GailLauncherPreview from "./GailLauncherPreview";
import GailLauncherTip from "./GailLauncherTip";
import {useFloatingClearance} from "@/hooks/useFloatingClearance";
import {useLauncherConversation} from "@/hooks/useLauncherConversation";
import {useLauncherPage} from "@/hooks/useLauncherPage";
import {useLauncherOnboarding} from "@/hooks/useLauncherOnboarding";
import {useGailLauncherShortcutHint} from "@/hooks/useGailLauncherShortcut";
import {LAUNCHER_PREVIEW_CLOSE_DELAY_MS, LAUNCHER_PREVIEW_OPEN_DELAY_MS} from "@/utils/chat/gailGptLauncher";

const LAUNCHER_HEIGHT_PX = 48;
const PREVIEW_GAP_PX = 12;
const TIP_GAP_PX = 16;
const canHover = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches === true;

export default function GailCornerLauncher() {
  const { t } = useTranslation("ask");
  const {open, openSheet, triggerRef, panelId, keyboardInteraction,
    setKeyboardInteraction, sheetProps} = useLauncherConversation((source) => {
      closePreview();
      acknowledgeTip(source === "tip" ? "try" : "open");
    });
  const [preview, setPreview] = useState<{ fromKeyboard: boolean } | null>(null);
  const bottomOffset = useFloatingClearance(!open);
  const previewRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const {shortcutHint, ariaKeyShortcuts} = useGailLauncherShortcutHint();
  const { heading, starters } = useLauncherPage();
  const {tipVisible, acknowledgeTip} = useLauncherOnboarding(open);
  const canPreview = starters.length > 0 && !tipVisible && !open;
  const previewOpen = preview !== null && canPreview;

  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };
  useEffect(() => clearTimers, []);

  const closePreview = useCallback((returnFocus = false) => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    // Focus inside a card that is going away would fall to the page; keep it on the launcher.
    if (returnFocus || previewRef.current?.contains(document.activeElement)) {
      triggerRef.current?.focus({ preventScroll: true });
    }
    setPreview(null);
  }, []);

  // The launcher and its card share one hover region, with a grace period to cross between them.
  const handlePointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    setKeyboardInteraction(false);
    window.clearTimeout(closeTimer.current);
    if (preview || !canPreview || !canHover()) return;
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => setPreview({ fromKeyboard: false }), LAUNCHER_PREVIEW_OPEN_DELAY_MS);
  };
  const handlePointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(() => closePreview(), LAUNCHER_PREVIEW_CLOSE_DELAY_MS);
  };

  const handleLauncherKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowUp" && canPreview) {
      event.preventDefault();
      clearTimers();
      setPreview({ fromKeyboard: true });
    } else if (event.key === "Escape" && preview) {
      event.preventDefault();
      closePreview();
    }
  };
  const focusStaysWithin = (event: FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    return Boolean(next && (triggerRef.current?.contains(next) || previewRef.current?.contains(next)));
  };
  const handleBlur = (event: FocusEvent) => {
    if (preview && !focusStaysWithin(event)) closePreview();
  };

  const popoverBase = bottomOffset + LAUNCHER_HEIGHT_PX;

  const button = (
    <GailAssistantLauncherButton
      ref={triggerRef}
      label={t("launcher.label")}
      highlighted={tipVisible}
      aria-controls={panelId}
      aria-expanded={open}
      aria-hidden={open}
      aria-keyshortcuts={ariaKeyShortcuts}
      data-open={open}
      data-instant-motion={keyboardInteraction}
      inert={open}
      onClick={() => openSheet(undefined, "launcher")}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
      onKeyDown={handleLauncherKeyDown}
      style={{ bottom: bottomOffset }}
      className={clsx("fixed right-6 z-40", open && "pointer-events-none")}
    />
  );

  return (
    <>
      {/* Mount before the delayed text arrives so the nudge is announced without moving focus. */}
      <div role="status" aria-atomic="true" className="sr-only">
        {tipVisible ? `${t("launcher.tip.title")} ${t("launcher.tip.body")}` : ""}
      </div>
      <GailAssistantSheet {...sheetProps} />
      <AnimatePresence custom={keyboardInteraction}>
        {previewOpen && (
          <GailLauncherPreview
            key="preview"
            ref={previewRef}
            heading={heading}
            starters={starters}
            shortcutHint={shortcutHint}
            bottom={popoverBase + PREVIEW_GAP_PX}
            focusFirstStarter={preview.fromKeyboard}
            instantMotion={keyboardInteraction}
            onSelect={(starter) => openSheet({ text: starter.text, mode: "send" }, "launcher")}
            onAskAnything={() => openSheet(undefined, "launcher")}
            onEscape={() => closePreview(true)}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onBlur={handleBlur}
          />
        )}
        {tipVisible && (
          <GailLauncherTip
            key="tip"
            bottom={popoverBase + TIP_GAP_PX}
            onDismiss={() => {
              triggerRef.current?.focus({ preventScroll: true });
              acknowledgeTip("dismiss");
            }}
            onTry={() => openSheet(undefined, "tip")}
          />
        )}
      </AnimatePresence>
      {button}
    </>
  );
}

